import type BaseDto from '../../model/interface/IBaseDto';
import type { INSTANTIATOR_INTERFACES } from '../../model/interface/instantiator';
import instantiator from '../../model/util/instantiator';
import type { ITable } from '../../private/db/interface';
import type { IWorkspaceTable } from '../interface/table';

/* Api data from serve response for type of Interface */
type ApiData<Interface extends keyof INSTANTIATOR_INTERFACES> = Parameters<
    INSTANTIATOR_INTERFACES[Interface]
>[0];

function _isEntity(object: unknown): object is BaseDto<never> {
    if (object === null || object === undefined || typeof object !== 'object') {
        return false;
    }

    const record: Partial<BaseDto<never>> = object as Record<string, unknown>;

    return (
        typeof record.flatten === 'function' &&
        typeof record.apply === 'function' &&
        typeof record.mappings === 'function'
    );
}

class ContainerTable<
    Interface extends keyof INSTANTIATOR_INTERFACES,
    Constructor extends INSTANTIATOR_INTERFACES[Interface],
    Entity extends ReturnType<Constructor>,
> implements IWorkspaceTable<Interface, Constructor, Entity>
{
    private _table: ITable<ApiData<Interface>, keyof ApiData<Interface>>;

    private _entityName: Interface;

    constructor(options: {
        table: ITable<ApiData<Interface>, keyof ApiData<Interface>>;
        name: keyof INSTANTIATOR_INTERFACES;
    }) {
        this._table = options.table;
        this._entityName = options.name as Interface;
    }

    count(): Promise<number> {
        return this._table.count();
    }

    async first(): Promise<Entity> {
        const table = this._table;
        const entityName = this._entityName;
        const items = (await table.toArray()) as Partial<ApiData<Interface>>[];
        const primaryItem = items.at(0);

        if (items.length === 0 || !primaryItem) {
            throw new Error('No items found');
        }

        const item = instantiator.instantiate(entityName, primaryItem) as Entity | undefined;

        if (!item) {
            throw new Error(`Failed to instantiate element ${entityName}`);
        }

        return item;
    }

    async get<Key extends keyof ApiData<Interface>>(pk: Key): Promise<Entity> {
        const table = this._table;
        const entityName: Interface = this._entityName;
        const data = (await table.get(pk)) as Partial<ApiData<Interface>> | undefined;

        if (!data) {
            throw new Error(`Unable to instantiate item for pk:${String(pk)}`);
        }

        const item = instantiator.instantiate(entityName, data);

        return item as Entity;
    }

    async all(): Promise<Entity[]> {
        const table = this._table;
        const entityName = this._entityName;

        const array = (await table.toArray()) as Partial<ApiData<Interface>>[];

        const items: Entity[] = [];
        for (const x of array) {
            const item = instantiator.instantiate(entityName, x) as Entity | undefined;

            if (item) {
                items.push(item);
            }
        }

        return items;
    }

    async bulkAddOrUpdate(items: Readonly<(ApiData<Interface> | Entity)[]>): Promise<void> {
        const table = this._table;

        const insItems: ApiData<Interface>[] = items.map(item =>
            _isEntity(item) ? item.flatten() : item,
        );

        await table.bulkAddOrUpdate(insItems);
    }
}

function ctor<
    Interface extends keyof INSTANTIATOR_INTERFACES,
    Constructor extends INSTANTIATOR_INTERFACES[Interface],
    Entity extends ReturnType<Constructor>,
>(
    name: Interface,
    table: ITable<ApiData<Interface>, keyof ApiData<Interface>>,
): IWorkspaceTable<Interface, Constructor, Entity> {
    const object = new ContainerTable({
        name,
        table,
    });

    return object as IWorkspaceTable<Interface, Constructor, Entity>;
}

export default ctor;
